import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Tag, Typography, Card, message, Select, Modal, Descriptions, Switch, Tooltip, Collapse, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined, SendOutlined, EyeOutlined, SettingOutlined, InfoCircleOutlined, CheckCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }

  /* Fix Alert text colors for dark theme */
  .dark-alert {
    color: ${COLORS.textPrimary} !important;

    .ant-alert-message {
      color: ${COLORS.textPrimary} !important;
    }

    .ant-alert-description {
      color: ${COLORS.textSecondary} !important;
    }

    .ant-alert-icon {
      color: ${COLORS.accent} !important;
    }
  }

  /* Fix pagination text */
  .ant-pagination {
    color: ${COLORS.textSecondary};

    .ant-pagination-item {
      background-color: ${COLORS.bgSecondary};
      border-color: ${COLORS.border};

      a {
        color: ${COLORS.textPrimary};
      }

      &.ant-pagination-item-active {
        background-color: ${COLORS.accent};
        border-color: ${COLORS.accent};

        a {
          color: ${COLORS.bgPrimary};
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      background-color: ${COLORS.bgSecondary};
      border-color: ${COLORS.border};

      .ant-pagination-item-link {
        color: ${COLORS.textPrimary};
        background-color: ${COLORS.bgSecondary};
        border-color: ${COLORS.border};
      }
    }

    .ant-pagination-options {
      .ant-select-selector {
        background-color: ${COLORS.bgSecondary} !important;
        color: ${COLORS.textPrimary} !important;
        border-color: ${COLORS.border} !important;
      }
      .ant-select-arrow {
        color: ${COLORS.textSecondary} !important;
      }
    }

    .ant-pagination-total-text {
      color: ${COLORS.textSecondary};
    }
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const PreferencesContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 0 !important;
  }

  .ant-collapse {
    background: transparent !important;
    border: none !important;
  }

  .ant-collapse-item {
    border-bottom: none !important;
  }

  .ant-collapse-header {
    color: ${COLORS.textPrimary} !important;
    padding: 16px 20px !important;
  }

  .ant-collapse-content {
    background: transparent !important;
    border-top: 1px solid ${COLORS.border} !important;
  }

  .ant-collapse-content-box {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-collapse-header {
      padding: 12px 16px !important;
    }
    
    .ant-collapse-content-box {
      padding: 12px 16px !important;
    }
  }
`;

const PreferenceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid ${COLORS.border};

  &:last-child {
    margin-bottom: 0;
  }
`;

const PreferenceInfo = styled.div`
  flex: 1;
`;

const PreferenceTitle = styled.div`
  color: ${COLORS.textPrimary};
  font-weight: 500;
  font-size: 14px;
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const PreferenceDescription = styled.div`
  color: ${COLORS.textSecondary};
  font-size: 12px;
  margin-top: 4px;
`;

interface WebhookEventType {
  key: string;
  value: string;
  description: string;
}

interface WebhookPreferences {
  [key: string]: boolean;
}

const Webhooks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);
  
  // Webhook Preferences State
  const [preferences, setPreferences] = useState<WebhookPreferences>({});
  const [eventTypes, setEventTypes] = useState<WebhookEventType[]>([]);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [savingPreference, setSavingPreference] = useState<string | null>(null);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchWebhooks();
    fetchWebhookPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, search, statusFilter]);

  const fetchWebhookPreferences = async () => {
    try {
      setPreferencesLoading(true);
      const [prefsResponse, typesResponse] = await Promise.all([
        apiClient.get(API_ENDPOINTS.MERCHANT_GET_WEBHOOK_PREFERENCES),
        apiClient.get(API_ENDPOINTS.MERCHANT_GET_WEBHOOK_EVENT_TYPES),
      ]);

      if (prefsResponse.data?.status === 'success' && prefsResponse.data?.data) {
        setPreferences(prefsResponse.data.data.preferences || {});
      }

      if (typesResponse.data?.status === 'success' && typesResponse.data?.data) {
        setEventTypes(typesResponse.data.data.eventTypes || []);
      }
    } catch (error: any) {
      console.error('Error fetching webhook preferences:', error);
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handlePreferenceToggle = async (eventType: string, enabled: boolean) => {
    try {
      setSavingPreference(eventType);
      const response = await apiClient.put(API_ENDPOINTS.MERCHANT_UPDATE_WEBHOOK_PREFERENCES, {
        preferences: { [eventType]: enabled },
      });

      if (response.data?.status === 'success') {
        setPreferences((prev) => ({ ...prev, [eventType]: enabled }));
        message.success(`Webhook ${enabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        message.error('Failed to update preference');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update preference');
      console.error('Error updating preference:', error);
    } finally {
      setSavingPreference(null);
    }
  };

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        params.search = search;
      }
      if (statusFilter) {
        params.deliveryStatus = statusFilter;
      }

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_WEBHOOK_LOGS, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setWebhooks(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || paginator.totalCount || paginator.total || 0,
          });
        } else {
          setPagination((prev) => ({
            ...prev,
            total: (data.itemsList || []).length,
          }));
        }
      }
    } catch (error: any) {
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (webhookLogId: string) => {
    try {
      await apiClient.post(`${API_ENDPOINTS.MERCHANT_RETRY_WEBHOOK}/${webhookLogId}/retry`);
      message.success('Webhook retry initiated');
      fetchWebhooks();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to retry webhook');
      console.error('Error retrying webhook:', error);
    }
  };

  const handleViewDetails = async (webhookLogId: string) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_WEBHOOK_LOG.replace('{webhookLogId}', webhookLogId));
      if (response.data?.status === 'success' && response.data?.data) {
        setSelectedWebhook(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error: any) {
      message.error('Failed to fetch webhook details');
      console.error('Error fetching webhook details:', error);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: any = {
      PENDING: { color: 'orange', text: 'Pending' },
      SUCCESS: { color: 'green', text: 'Success' },
      FAILED: { color: 'red', text: 'Failed' },
      SKIPPED: { color: 'default', text: 'Skipped' },
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const enabledCount = Object.values(preferences).filter(Boolean).length;
  const totalCount = eventTypes.length;

  const columns = [
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Webhook URL',
      dataIndex: 'webhookUrl',
      key: 'webhookUrl',
      ellipsis: true,
      render: (url: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {url ? `${url.substring(0, 40)}...` : '-'}
        </span>
      ),
    },
    {
      title: 'Delivery Status',
      dataIndex: 'deliveryStatus',
      key: 'deliveryStatus',
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          SUCCESS: 'green',
          FAILED: 'red',
          PENDING: 'orange',
          SKIPPED: 'default',
        };
        return (
          <Tooltip title={status === 'SKIPPED' ? 'Webhook type was disabled when this event occurred' : undefined}>
            <Tag color={colorMap[status] || 'default'}>{status}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'HTTP Status',
      dataIndex: 'httpStatus',
      key: 'httpStatus',
      render: (status: number) => status ? <Tag>{status}</Tag> : '-',
    },
    {
      title: 'Last Attempt',
      dataIndex: 'lastAttemptAt',
      key: 'lastAttemptAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record.id)}
            size="small"
          >
            View
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={() => handleRetry(record.id)}
            size="small"
            danger={record.deliveryStatus === 'FAILED'}
            type={record.deliveryStatus === 'SUCCESS' ? 'default' : 'primary'}
          >
            Resend
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Webhooks
        </Title>
      </HeaderContainer>

      {/* Webhook Preferences Section */}
      <PreferencesContainer>
        <Collapse
          defaultActiveKey={[]}
          ghost
          expandIconPosition="end"
        >
          <Panel
            header={
              <Space>
                <SettingOutlined style={{ color: COLORS.accent }} />
                <span style={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                  Webhook Preferences
                </span>
                <Tag color={enabledCount === totalCount ? 'green' : 'orange'}>
                  {enabledCount}/{totalCount} enabled
                </Tag>
              </Space>
            }
            key="preferences"
          >
            <Alert
              message="Configure which webhook events you want to receive"
              description="Disabled webhooks will still be logged but won't be sent to your endpoint. You can view skipped webhooks in the logs below."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{
                marginBottom: 16,
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textPrimary
              }}
              className="dark-alert"
            />
            
            {preferencesLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: COLORS.textSecondary }}>
                Loading preferences...
              </div>
            ) : (
              eventTypes.map((eventType) => (
                <PreferenceItem key={eventType.value}>
                  <PreferenceInfo>
                    <PreferenceTitle>
                      {preferences[eventType.value] !== false ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      ) : (
                        <PauseCircleOutlined style={{ color: COLORS.textSecondary, marginRight: 8 }} />
                      )}
                      {eventType.value}
                    </PreferenceTitle>
                    <PreferenceDescription>{eventType.description}</PreferenceDescription>
                  </PreferenceInfo>
                  <Switch
                    checked={preferences[eventType.value] !== false}
                    loading={savingPreference === eventType.value}
                    onChange={(checked) => handlePreferenceToggle(eventType.value, checked)}
                  />
                </PreferenceItem>
              ))
            )}
          </Panel>
        </Collapse>
      </PreferencesContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Search</span>
              <Input
                placeholder="Search by event type"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => {
                  const newParams = new URLSearchParams(searchParams);
                  if (search) {
                    newParams.set('search', search);
                  } else {
                    newParams.delete('search');
                  }
                  setSearchParams(newParams);
                }}
                style={{ maxWidth: 300 }}
              />
            </div>
            <div>
              <span style={{ color: COLORS.textSecondary, marginRight: 8 }}>Status</span>
              <Select
                placeholder="Filter by status"
                value={statusFilter || undefined}
                onChange={(value) => {
                  setStatusFilter(value);
                  const newParams = new URLSearchParams(searchParams);
                  if (value) {
                    newParams.set('status', value);
                  } else {
                    newParams.delete('status');
                  }
                  setSearchParams(newParams);
                }}
                allowClear
                style={{ width: 150 }}
              >
                <Select.Option value="SUCCESS">Success</Select.Option>
                <Select.Option value="FAILED">Failed</Select.Option>
                <Select.Option value="PENDING">Pending</Select.Option>
                <Select.Option value="SKIPPED">Skipped</Select.Option>
              </Select>
            </div>
            <Button icon={<ReloadOutlined />} onClick={fetchWebhooks} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={webhooks}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => <span style={{ color: COLORS.textSecondary }}>Total {total} webhooks</span>,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Webhook Log Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button
            key="retry"
            type={selectedWebhook?.deliveryStatus === 'FAILED' ? 'primary' : 'default'}
            danger={selectedWebhook?.deliveryStatus === 'FAILED'}
            icon={<SendOutlined />}
            onClick={() => {
              if (selectedWebhook) {
                handleRetry(selectedWebhook.id);
                setDetailModalVisible(false);
              }
            }}
          >
            Resend Webhook
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        style={{ top: 20 }}
      >
        {selectedWebhook && (
          <Descriptions
            column={1}
            bordered
            labelStyle={{
              background: COLORS.bgPrimary,
              color: COLORS.textPrimary,
              borderColor: COLORS.border,
            }}
            contentStyle={{
              background: COLORS.bgSecondary,
              color: COLORS.textPrimary,
              borderColor: COLORS.border,
            }}
          >
            <Descriptions.Item label="Event Type">{selectedWebhook.eventType}</Descriptions.Item>
            <Descriptions.Item label="Delivery Status">
              {getStatusTag(selectedWebhook.deliveryStatus)}
              {selectedWebhook.deliveryStatus === 'SKIPPED' && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  (Webhook type was disabled)
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="HTTP Status">{selectedWebhook.httpStatus || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Retry Count">{selectedWebhook.retryCount || 0}</Descriptions.Item>
            <Descriptions.Item label="Webhook URL">{selectedWebhook.webhookUrl}</Descriptions.Item>
            <Descriptions.Item label="Error Message">
              {selectedWebhook.errorMessage || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Response Body">
              <pre style={{ 
                background: COLORS.bgPrimary, 
                padding: 8, 
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 200,
                color: COLORS.textPrimary,
              }}>
                {selectedWebhook.responseBody ? (typeof selectedWebhook.responseBody === 'string' ? selectedWebhook.responseBody : JSON.stringify(selectedWebhook.responseBody, null, 2)) : 'N/A'}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="Payload">
              <pre style={{ 
                background: COLORS.bgPrimary, 
                padding: 8, 
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 200,
                color: COLORS.textPrimary,
              }}>
                {JSON.stringify(selectedWebhook.payload, null, 2)}
              </pre>
            </Descriptions.Item>
            {selectedWebhook.headers && (
              <Descriptions.Item label="Headers">
                <pre style={{ 
                  background: COLORS.bgPrimary, 
                  padding: 8, 
                  borderRadius: 4,
                  overflow: 'auto',
                  maxHeight: 200,
                  color: COLORS.textPrimary,
                }}>
                  {JSON.stringify(selectedWebhook.headers, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Created At">
              {new Date(selectedWebhook.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Last Attempt At">
              {selectedWebhook.lastAttemptAt ? new Date(selectedWebhook.lastAttemptAt).toLocaleString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Delivered At">
              {selectedWebhook.deliveredAt ? new Date(selectedWebhook.deliveredAt).toLocaleString() : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </PageContainer>
  );
};

export default Webhooks;
